import { AsyncAiProcessStatusEnum } from "@/lib/enum/async-ai-process-status.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatCurrency } from "@/lib/utility/currency-helper";
import documentRuleCheckService from "@/services/mrfiktiv/services/documentRuleCheckService";
import {
  MrfiktivAsyncAiTaskStatusLogElementViewModelGen,
  MrfiktivDocumentRuleCheckViewModelGen,
  MrfiktivOpenAiAnalysisCostViewModelGen,
  MrfiktivOpenAiDocumentRuleEvaluationViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { DocumentRuleCheckDataAccessLayer } from "@/store/modules/access-layers/document-rule-check.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { ISignDocument, SignDocument } from "./sign-document.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { ClosedByAt, IClosedByAt } from "./by-at.entity";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";

@IsFilterable
class DocumentRuleCheckBase implements IEntity<IDocumentRuleCheck>, MrfiktivDocumentRuleCheckViewModelGen {
  id: string;
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.anonymization.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  userId: string;

  statusLog: MrfiktivAsyncAiTaskStatusLogElementViewModelGen[];
  evaluations: MrfiktivOpenAiDocumentRuleEvaluationViewModelGen[];
  documentId: string;
  ruleSetId?: string;

  costAnalysis?: MrfiktivOpenAiAnalysisCostViewModelGen;

  totalMonetaryImpact?: number;

  document: ISignDocument;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  @FilterConfig({
    type: ClosedByAt,
    displayName: "objects.closedByAt"
  })
  closed?: IClosedByAt;

  get isClosed(): boolean {
    return !!this.closed?.at;
  }

  loading = false;

  get status() {
    const last = this.statusLog[this.statusLog.length - 1];
    return last?.status;
  }

  get successRate() {
    return (this.successChecks.length / (this.failedChecks.length + this.successChecks.length)) * 100;
  }

  get failedChecks() {
    return this.evaluations.filter((item) => item.isFulfilled === false && item.isRelevant === true);
  }

  get successChecks() {
    return this.evaluations.filter((item) => item.isFulfilled === true && item.isRelevant === true);
  }

  get notRelevantChecks() {
    return this.evaluations.filter((item) => item.isRelevant === false);
  }

  get fileName() {
    if (this.document?.name) {
      return this.document.name;
    }

    return "";
  }

  /**
   * Returns the total amount formatted as a currency string in Euros.
   * If the totalAmount is undefined, null, or 0, it returns an empty string.
   */
  get totalAmountAsString(): string {
    if (!this.costAnalysis?.total) {
      return "";
    }
    return formatCurrency(this.costAnalysis.total, CurrencyEnum.EUR);
  }

  /**
   * Returns the signed sum of per-evaluation monetary impacts formatted as
   * EUR. Falls back to 0,00 € when the backend has not computed a value.
   */
  get totalMonetaryImpactAsString(): string {
    return formatCurrency(this.totalMonetaryImpact ?? 0, CurrencyEnum.EUR);
  }

  /**
   * Sortable score for the Potenzial column. Descending order reads
   * "biggest potential first": monetary impact (high → low) → unquantified
   * findings → compliant. Incomplete checks return undefined so they cluster
   * at one end consistently.
   */
  get potentialScore(): number | undefined {
    if (!this.isCompleted) return undefined;
    const impact = this.totalMonetaryImpact ?? 0;
    if (impact > 0) return impact;
    if (this.failedChecks.length > 0) return 0;
    return -1;
  }

  get costTags(): string[] {
    if (this.costAnalysis?.tags) {
      return this.costAnalysis?.tags;
    }
    return [];
  }

  get documentNumber(): string | undefined {
    if (this.costAnalysis?.title) {
      return this.costAnalysis?.title;
    }
  }

  constructor(data: Partial<MrfiktivDocumentRuleCheckViewModelGen>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.statusLog = data.statusLog || [];
    this.evaluations = data.evaluations || [];
    this.documentId = data.documentId || "";
    this.ruleSetId = data.ruleSetId;
    this.costAnalysis = data.costAnalysis;
    this.totalMonetaryImpact = data.totalMonetaryImpact;
    this.document = new SignDocument(data?.document);
    this.timestamp = new Timestamp(data.timestamp);
    this.closed = data.closed ? new ClosedByAt(data.closed) : undefined;
  }

  map(data: MrfiktivDocumentRuleCheckViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.userId = data.userId;
    this.statusLog = data.statusLog;
    this.evaluations = data.evaluations;
    this.documentId = data.documentId;
    this.ruleSetId = data.ruleSetId;
    this.costAnalysis = data.costAnalysis;
    this.totalMonetaryImpact = data.totalMonetaryImpact;
    this.document = new SignDocument(data?.document);
    this.timestamp = new Timestamp(data.timestamp);
    this.closed = data.closed ? new ClosedByAt(data.closed) : undefined;
  }

  async create(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    this.map(
      await documentRuleCheckService.create(this.partnerId, {
        documentId: this.documentId,
        ruleSetId: this.ruleSetId
      })
    );

    DocumentRuleCheckDataAccessLayer.set(this);
    return this;
  }

  async fetch(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    if (!this.id) {
      throw new Error("Id not set");
    }
    this.map(await documentRuleCheckService.getOne(this.partnerId, this.id));
    DocumentRuleCheckDataAccessLayer.set(this);
    return this;
  }

  async close(): Promise<this> {
    this.map(await documentRuleCheckService.close(this.partnerId, this.id));
    DocumentRuleCheckDataAccessLayer.set(this);
    return this;
  }

  async reopen(): Promise<this> {
    this.map(await documentRuleCheckService.reopen(this.partnerId, this.id));
    DocumentRuleCheckDataAccessLayer.set(this);
    return this;
  }

  async delete(): Promise<void> {
    await documentRuleCheckService.delete(this.partnerId, this.id);
    DocumentRuleCheckDataAccessLayer.delete(this);
  }

  get isPending() {
    return this.status === AsyncAiProcessStatusEnum.PENDING;
  }

  get isInProgress() {
    return this.status === AsyncAiProcessStatusEnum.IN_PROGRESS;
  }

  get isCompleted() {
    return this.status === AsyncAiProcessStatusEnum.COMPLETED;
  }

  get isFailed() {
    return this.status === AsyncAiProcessStatusEnum.FAILED;
  }

  get totalTime(): number | null {
    return this.calculateTime(this.statusLog, AsyncAiProcessStatusEnum.PENDING, AsyncAiProcessStatusEnum.COMPLETED);
  }

  get calculationTime(): number | null {
    return this.calculateTime(this.statusLog, AsyncAiProcessStatusEnum.IN_PROGRESS, AsyncAiProcessStatusEnum.COMPLETED);
  }

  private calculateTime(
    statusLog: MrfiktivAsyncAiTaskStatusLogElementViewModelGen[],
    startStatus: AsyncAiProcessStatusEnum,
    endStatus: AsyncAiProcessStatusEnum
  ): number | null {
    const startTime = statusLog.find((log) => log.status === startStatus)?.time;
    const endTime = statusLog.find((log) => log.status === endStatus)?.time;

    if (startTime && endTime) {
      return (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000; // Convert milliseconds to seconds
    }

    return null;
  }
}

type IDocumentRuleCheck = DocumentRuleCheckBase;
const DocumentRuleCheck = Filter.createForClass(DocumentRuleCheckBase);

export { DocumentRuleCheck, IDocumentRuleCheck };
